// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useMemo } from 'react';
import type { ComplicationCategory, TimeSeriesData } from '../../lib/models';
import { expandBandsToCycles } from '../../../../facade/vaccineFacade';
import './PerCycleTrace.css';

export interface ComplicationsTableProps {
  series: TimeSeriesData[];
  complications: ComplicationCategory[];
}

interface Row {
  id: string;
  name: string;
  noVacCount: number;
  noVacCost: number;
  vacCount: number;
  vacCost: number;
  avertedCount: number;
  avertedCost: number;
}

const oneDecimalFmt = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const eurFmt = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

const formatCount = (v: number): string => oneDecimalFmt.format(v);
const formatEur = (v: number): string => eurFmt.format(v);

const computeRow = (
  comp: ComplicationCategory,
  series: TimeSeriesData[],
): Row => {
  const cycles = series.length;
  const incPerCycle = expandBandsToCycles(comp.incidencePctBands ?? [], cycles);
  const costPerCycle = expandBandsToCycles(comp.costPerCaseBands ?? [], cycles);
  let noVacCount = 0;
  let noVacCost = 0;
  let vacCount = 0;
  let vacCost = 0;
  for (let k = 0; k < cycles; k++) {
    const incFrac = Math.max(0, Math.min(100, incPerCycle[k] ?? 0)) / 100;
    const cost = Math.max(0, costPerCycle[k] ?? 0);
    const baselineCases = series[k]?.baselineCases ?? 0;
    const withVacCases = series[k]?.withVaccineCases ?? 0;
    const cN = baselineCases * incFrac;
    const cV = withVacCases * incFrac;
    noVacCount += cN;
    noVacCost += cN * cost;
    vacCount += cV;
    vacCost += cV * cost;
  }
  return {
    id: comp.id,
    name: comp.name,
    noVacCount,
    noVacCost,
    vacCount,
    vacCost,
    avertedCount: noVacCount - vacCount,
    avertedCost: noVacCost - vacCost,
  };
};

const buildCsv = (rows: Row[]): string => {
  const header = [
    'Complication',
    'No-vaccine count',
    'No-vaccine cost (EUR)',
    'With-vaccine count',
    'With-vaccine cost (EUR)',
    'Averted count',
    'Averted cost (EUR)',
  ].join(',');
  const escape = (s: string): string =>
    /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  const body = rows.map((r) =>
    [
      escape(r.name),
      r.noVacCount.toFixed(2),
      r.noVacCost.toFixed(2),
      r.vacCount.toFixed(2),
      r.vacCost.toFixed(2),
      r.avertedCount.toFixed(2),
      r.avertedCost.toFixed(2),
    ].join(','),
  );
  const totals = rows.reduce(
    (acc, r) => ({
      noVacCount: acc.noVacCount + r.noVacCount,
      noVacCost: acc.noVacCost + r.noVacCost,
      vacCount: acc.vacCount + r.vacCount,
      vacCost: acc.vacCost + r.vacCost,
      avertedCount: acc.avertedCount + r.avertedCount,
      avertedCost: acc.avertedCost + r.avertedCost,
    }),
    {
      noVacCount: 0,
      noVacCost: 0,
      vacCount: 0,
      vacCost: 0,
      avertedCount: 0,
      avertedCost: 0,
    },
  );
  const totalRow = [
    'Total',
    totals.noVacCount.toFixed(2),
    totals.noVacCost.toFixed(2),
    totals.vacCount.toFixed(2),
    totals.vacCost.toFixed(2),
    totals.avertedCount.toFixed(2),
    totals.avertedCost.toFixed(2),
  ].join(',');
  return [header, ...body, totalRow].join('\n');
};

const downloadCsv = (filename: string, content: string): void => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const ComplicationsTable: React.FC<ComplicationsTableProps> = ({
  series,
  complications,
}) => {
  const rows = useMemo(() => {
    const enabled = (complications ?? []).filter(
      (c) =>
        c &&
        c.enabled !== false &&
        typeof c.name === 'string' &&
        c.name.trim().length > 0,
    );
    return enabled.map((c) => computeRow(c, series));
  }, [complications, series]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => ({
          noVacCount: acc.noVacCount + r.noVacCount,
          noVacCost: acc.noVacCost + r.noVacCost,
          vacCount: acc.vacCount + r.vacCount,
          vacCost: acc.vacCost + r.vacCost,
          avertedCount: acc.avertedCount + r.avertedCount,
          avertedCost: acc.avertedCost + r.avertedCost,
        }),
        {
          noVacCount: 0,
          noVacCost: 0,
          vacCount: 0,
          vacCost: 0,
          avertedCount: 0,
          avertedCost: 0,
        },
      ),
    [rows],
  );

  if (rows.length === 0) {
    return (
      <div className="per-cycle-trace">
        <p className="per-cycle-trace__placeholder">
          No complications configured. Add at least one named, enabled
          complication in the Model inputs panel to see per-complication counts
          and costs here.
        </p>
      </div>
    );
  }

  if (!series.length) {
    return (
      <div className="per-cycle-trace">
        <p className="per-cycle-trace__placeholder">
          Per-cycle results not available. Run a multi-year computation to
          populate cohort cases.
        </p>
      </div>
    );
  }

  return (
    <div className="per-cycle-trace">
      <div className="per-cycle-trace__header">
        <p className="per-cycle-trace__hint">
          Expected number of complications and total cost over the time
          horizon, derived from per-cycle cases × incidence × cost-per-case for
          each enabled complication. Counts and costs are{' '}
          <strong>undiscounted</strong>.
        </p>
        <button
          type="button"
          className="per-cycle-trace__csv-button"
          onClick={() => downloadCsv('complications.csv', buildCsv(rows))}
        >
          Download CSV
        </button>
      </div>

      <div className="per-cycle-trace__scroll">
        <table className="per-cycle-trace__table">
          <colgroup>
            <col />
          </colgroup>
          <colgroup className="per-cycle-trace__group">
            <col />
            <col />
          </colgroup>
          <colgroup className="per-cycle-trace__group">
            <col />
            <col />
          </colgroup>
          <colgroup className="per-cycle-trace__group">
            <col className="per-cycle-trace__col--delta" />
            <col className="per-cycle-trace__col--delta" />
          </colgroup>
          <thead>
            <tr className="per-cycle-trace__group-row">
              <th aria-hidden="true" />
              <th colSpan={2} scope="colgroup">
                No vaccine
                <span className="per-cycle-trace__badge per-cycle-trace__badge--neutral">
                  counts
                </span>
              </th>
              <th colSpan={2} scope="colgroup">
                With vaccine
                <span className="per-cycle-trace__badge per-cycle-trace__badge--neutral">
                  counts
                </span>
              </th>
              <th colSpan={2} scope="colgroup">
                Averted
                <span className="per-cycle-trace__badge per-cycle-trace__badge--undiscounted">
                  no-vac − vac
                </span>
              </th>
            </tr>
            <tr className="per-cycle-trace__head-row">
              <th scope="col">Complication</th>
              <th scope="col">Count</th>
              <th scope="col">Cost</th>
              <th scope="col">Count</th>
              <th scope="col">Cost</th>
              <th scope="col">Count</th>
              <th scope="col">Cost</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <th scope="row">{r.name}</th>
                <td>{formatCount(r.noVacCount)}</td>
                <td>{formatEur(r.noVacCost)}</td>
                <td>{formatCount(r.vacCount)}</td>
                <td>{formatEur(r.vacCost)}</td>
                <td className="per-cycle-trace__cell--delta">
                  {formatCount(r.avertedCount)}
                </td>
                <td className="per-cycle-trace__cell--delta">
                  {formatEur(r.avertedCost)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="per-cycle-trace__total-row">
              <th scope="row">Total</th>
              <td>{formatCount(totals.noVacCount)}</td>
              <td>{formatEur(totals.noVacCost)}</td>
              <td>{formatCount(totals.vacCount)}</td>
              <td>{formatEur(totals.vacCost)}</td>
              <td className="per-cycle-trace__cell--delta">
                {formatCount(totals.avertedCount)}
              </td>
              <td className="per-cycle-trace__cell--delta">
                {formatEur(totals.avertedCost)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default ComplicationsTable;
